import type { Option } from '@openx/types/options';
import type { SegmentsOptions } from '@openx/types/targeting/customVariables';
import { useMemo } from 'react';
import { useTargetingContext } from '../../../utils/context/TargetingContext';
import {
  buildContextualSegmentFetchFilters,
  type ContextualSegmentFilterValues,
  mergeContextualDataPartnerOptions,
} from '../drawer/hooks/useContextualSegmentFilters';
import { useContextualSegmentsPagination } from '../drawer/hooks/useContextualSegmentsPagination';
import { normalizeProviderId } from '../utils/enrichment';
import { useContextualAccount } from './useContextualAccount';

export type UseGetContextualData = (
  accountUid: string,
  selectedSegmentIds?: string[],
  filterValues?: ContextualSegmentFilterValues
) => {
  loading: boolean;
  providerIds: string[];
  dataPartnerOptions: Option[];
  dataPartnerOptionsLoading: boolean;
  hasOwnSegments: boolean;
  providerName?: string;
  segments: SegmentsOptions;
  allSegments?: SegmentsOptions;
  selectedSegments: SegmentsOptions;
  /** Loading of the data the misconfiguration check depends on - unlike `loading`, it ignores list (search/filter) requests. */
  misconfigurationLoading: boolean;
  totalCount: number;
  loadingMore: boolean;
  hasMore: boolean;
  handleLoadMore: () => void;
};

export const useGetContextualData: UseGetContextualData = (accountUid, selectedSegmentIds = [], filterValues) => {
  const { useContextualDataPartnerOptions, useFetchSegments } = useTargetingContext();
  const { accountId, loading: organizationsLoading, providerIds } = useContextualAccount(accountUid);
  const { dataPartnerOptions: sellableDataPartnerOptions, dataPartnerOptionsLoading } =
    useContextualDataPartnerOptions();
  const dataPartnerOptions = useMemo(
    () => mergeContextualDataPartnerOptions(sellableDataPartnerOptions, providerIds),
    [providerIds, sellableDataPartnerOptions]
  );
  const ownProviderIds = providerIds.map(normalizeProviderId).filter(Boolean);
  const { segments: ownSegments } = useFetchSegments({
    account_id: ownProviderIds.length ? accountId : undefined,
    provider_id: ownProviderIds,
    limit: 1,
    offset: 0,
  });
  const fetchFilters = filterValues
    ? buildContextualSegmentFetchFilters(filterValues.phrase, filterValues.source, filterValues.filters, providerIds)
    : {};
  const {
    segments,
    totalCount,
    loading: pageLoading,
    loadingMore,
    hasMore,
    handleLoadMore,
  } = useContextualSegmentsPagination({ accountId, filters: fetchFilters });
  // A single segment id can have a record under each provider, so the limit has to leave room for
  // every variant - a truncated response would look like missing metadata and raise a false warning.
  const { segments: selectedSegments, loading: selectedSegmentsLoading } = useFetchSegments({
    account_id: accountId,
    segment_id: selectedSegmentIds,
    limit: Math.max(selectedSegmentIds.length * Math.max(providerIds.length, 1), 1),
    offset: 0,
  });

  const allSegments = useMemo(() => [...selectedSegments, ...segments], [selectedSegments, segments]);
  const misconfigurationLoading = organizationsLoading || (selectedSegmentIds.length > 0 && selectedSegmentsLoading);
  const loading = misconfigurationLoading || pageLoading;

  return {
    loading,
    providerIds,
    dataPartnerOptions,
    dataPartnerOptionsLoading,
    hasOwnSegments: ownSegments.length > 0,
    providerName: ownSegments[0]?.provider_name,
    segments,
    allSegments,
    selectedSegments,
    misconfigurationLoading,
    totalCount,
    loadingMore,
    hasMore,
    handleLoadMore,
  };
};
